import { ChangeEvent } from "react";
import { toast } from "react-toastify";
import moment from "jalali-moment";

import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Button, Grid, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SystemUpdateAltOutlinedIcon from "@mui/icons-material/SystemUpdateAltOutlined";
import { PASSENGER_STATUS, TRIP_TYPES_DATA, VEHICLE_TYPES_DATA } from "Constants/Survey";
import { ISurvey } from "Types/survey";

import { useAppDispatch, useAppSelector } from "Hooks/useRedux";
import useSurveys from "Hooks/useSurveys";

import { changeDate, changeSurveysFilter } from "Redux/filtersSlice";
import { changeSelectLocFor, changeSelectLocMode, changeShowPin, setMapData } from "Redux/mapSlice";
import { changeMenu } from "Redux/menuSlice";
import { setSurvay } from "Redux/singleLocationSlice";

import Api from "Api";

import { genderToPersian } from "Helper/general";

import DatePicker from "Components/DatePicker";
import DayPicker from "Components/DayPicker";
import TimePicker from "Components/TimePicker";

import Header from "./Header";
import Table, { StyledTableCell } from "./Table";

export default function Survey() {
  const { surveys, date } = useAppSelector((state) => state.filters);

  const dispatch = useAppDispatch();

  const { data, isLoading, refetch, isFetching } = useSurveys();

  const weekDays = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

  const showOnMap = (survey: ISurvey) => {
    dispatch(changeMenu("disable"));
    dispatch(setSurvay([survey]));
    dispatch(
      setMapData({
        bounds: {
          positions: [
            [survey.source_lat, survey.source_lng],
            [survey.destination_lat, survey.destination_lng],
          ],
        },
      })
    );
  };
  const filterList = () => {
    refetch();
  };
  const handleChangePassengerStatus = (id: number, e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    Api.post(`/survey/set-passenger-status/${id}`, { status: e.target.value }).then((res) => {
      toast.success("وضعیت با موفقیت تغییر کرد");
      filterList();
    });
  };
  const rowContent = (_index: number) => {
    if (!data) return;

    const item = data[_index];
    const surveyTypeData = TRIP_TYPES_DATA[item?.trip_type];
    const vehicleTypeData = VEHICLE_TYPES_DATA[item?.vehicle_type];

    return (
      <>
        <StyledTableCell className="fa-number" style={{ width: "100px" }}>
          {_index + 1}
        </StyledTableCell>
        <StyledTableCell className="fa-number">{item?.id}</StyledTableCell>
        <StyledTableCell>{`${item?.first_name || "-"} ${item?.last_name}`}</StyledTableCell>
        <StyledTableCell>{genderToPersian(item?.gender)}</StyledTableCell>
        <StyledTableCell className="fa-number">{item?.mobile}</StyledTableCell>
        <StyledTableCell className="fa-number">
          <TextField
            select
            value={item?.passenger_status}
            size="small"
            onChange={(value) => handleChangePassengerStatus(item?.id, value)}
            sx={{ width: "120px" }}
          >
            {Object.keys(PASSENGER_STATUS).map((key) => (
              <MenuItem value={key}>{PASSENGER_STATUS[key as keyof typeof PASSENGER_STATUS]}</MenuItem>
            ))}
          </TextField>
        </StyledTableCell>
        <StyledTableCell className="fa-number">
          {item?.created_at ? moment(item.created_at).locale("fa").format("jYYYY/jMM/jDD HH:mm:ss") : null}
        </StyledTableCell>
        <StyledTableCell className="fa-number">
          {item?.updated_at ? moment(item.updated_at).locale("fa").format("jYYYY/jMM/jDD HH:mm:ss") : null}
        </StyledTableCell>
        <StyledTableCell className="fa-number">
          <Stack direction="row">
            {weekDays.map((d, i) => (
              <div
                style={{
                  color: item?.working_days.includes(i + 1) ? "#fff" : "#000",
                  border: "1px solid #555",
                  padding: "4px",
                  minWidth: "25px",
                  textAlign: "center",
                  backgroundColor: item?.working_days.includes(i + 1) ? "#39BCA3" : "#fff",
                }}
              >
                {d}
              </div>
            ))}
          </Stack>
        </StyledTableCell>
        <StyledTableCell className="fa-number">{item?.go_time}</StyledTableCell>
        <StyledTableCell className="fa-number">{item?.return_time}</StyledTableCell>
        {/* <StyledTableCell className="fa-number">-</StyledTableCell> */}
        <StyledTableCell>{surveyTypeData?.text || "-"}</StyledTableCell>
        <StyledTableCell>{vehicleTypeData?.name}</StyledTableCell>
        <StyledTableCell className="fa-number">{item?.trip_price}</StyledTableCell>
        <StyledTableCell>
          <IconButton color="primary" onClick={() => showOnMap(item)}>
            <LocationOnIcon />
          </IconButton>
        </StyledTableCell>
      </>
    );
  };
  const handleSetFilter = (key: string, value: any) => {
    if (value) {
      dispatch(changeSurveysFilter({ [key]: value }));
    }
  };
  const handleSetDateRange = (obj: any) => {
    dispatch(changeDate(obj));
    // dispatch(changeSurveysFilter(obj));
  };
  const handleChangeFilters = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    handleSetFilter(name, value);
  };

  const handleAddSurvay = () => {
    dispatch(changeMenu("disable"));
    dispatch(changeShowPin(true));
    dispatch(changeSelectLocMode(1));
    dispatch(changeSelectLocFor("add-survay"));
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Header>
        <Stack sx={{ flexGrow: 1 }} direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" sx={{ fontWeight: "800" }}>
            مسیرهای ثبتی شهروندان
          </Typography>

          <Typography sx={{ fontWeight: "500" }} className="fa-number">
            تعداد مسیرهای ثبتی شهروندان: {data?.length || 0}
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button variant="white" size="large">
              <LocalPrintshopOutlinedIcon />
            </Button>
            <Button variant="white" size="large">
              <SystemUpdateAltOutlinedIcon />
            </Button>
          </Stack>
        </Stack>
      </Header>

      <Grid container spacing={1} mb={2}>
        <Grid item xs={12} md={1.1}>
          <TextField fullWidth label="کد، نام مسیر، موبایل" name="q" onChange={handleChangeFilters} />
        </Grid>
        <Grid item xs={12} md={1}>
          <TextField fullWidth label="نوع سفر" select name="trip_type" onChange={handleChangeFilters}>
            <MenuItem value="">همه</MenuItem>
            {Object.keys(TRIP_TYPES_DATA).map((key) => {
              return <MenuItem value={key}>{TRIP_TYPES_DATA[key as keyof typeof TRIP_TYPES_DATA].text}</MenuItem>;
            })}
          </TextField>
        </Grid>
        <Grid item xs={12} md={1.7}>
          <DatePicker
            label="تاریخ ثبت"
            name="date"
            // data={surveys}
            data={date}
            isRange={true}
            setData={(value) => handleSetDateRange(value)}
          />
        </Grid>
        <Grid item xs={12} md={1.7}>
          <DayPicker
            label="روزهای هفته"
            multiple
            padding="14.5px 8px"
            minWidth={35}
            isGeneral={true}
            value={surveys?.working_days || []}
            setData={(value) => handleSetFilter("working_days", value)}
          />
        </Grid>
        <Grid item xs={12} md={1.5}>
          <TextField fullWidth label="وسیله نقلیه" select name="vehicle_type" onChange={handleChangeFilters}>
            <MenuItem value="">همه</MenuItem>
            {Object.keys(VEHICLE_TYPES_DATA).map((key) => {
              return <MenuItem value={key}>{VEHICLE_TYPES_DATA[key as keyof typeof VEHICLE_TYPES_DATA].name}</MenuItem>;
            })}
          </TextField>
        </Grid>
        <Grid item xs={12} md={0.8}>
          <TimePicker label=" رسیدن از" name="go_after" data={surveys} setData={(value) => handleSetFilter("go_after", value)} />
        </Grid>
        <Grid item xs={12} md={0.8}>
          <TimePicker
            label=" رسیدن تا"
            name="go_before"
            data={surveys}
            setData={(value) => handleSetFilter("go_before", value)}
          />
        </Grid>
        <Grid item xs={12} md={0.9}>
          <TimePicker
            label=" برگشت از"
            name="return_after"
            data={surveys}
            setData={(value) => handleSetFilter("return_after", value)}
          />
        </Grid>
        <Grid item xs={12} md={0.9}>
          <TimePicker
            label=" برگشت تا"
            name="return_before"
            data={surveys}
            setData={(value) => handleSetFilter("return_before", value)}
          />
        </Grid>
        <Grid item xs={12} md={0.9}>
          <LoadingButton loading={isFetching} fullWidth variant="contained" size="large" onClick={filterList}>
            فیلتر
          </LoadingButton>
        </Grid>
        <Grid item xs={12} md={0.7}>
          <LoadingButton
            loading={isFetching}
            fullWidth
            variant="contained"
            size="large"
            onClick={handleAddSurvay}
            style={{ backgroundColor: "green" }}
          >
            افزودن
          </LoadingButton>
        </Grid>
      </Grid>

      <Table
        headerColumns={[
          { title: "ردیف", style: { width: "4%" } },
          { title: "کد", style: {} },
          { title: "نام مسافر", style: {} },
          { title: "جنسیت", style: { width: "4%" } },
          { title: "موبایل", style: {} },
          { title: "وضعیت مسافر", style: {} },
          { title: "تاریخ ثبت", style: {} },
          { title: "تاریخ بروزرسانی", style: {} },
          { title: "روزها", style: { width: "11%" } },
          { title: "ساعت رسیدن", style: { width: "5%" } },
          { title: "ساعت برگشت", style: { width: "5%" } },
          { title: "نوع سفر", style: {} },
          { title: "وسیله نقلیه", style: {} },
          { title: "هزینه", style: { width: "5%" } },
          { title: "عملیات", style: { width: "5%" } },
        ]}
        isLoading={isLoading}
        data={data}
        rowContent={rowContent}
      />
    </Box>
  );
}
